import React, { useState } from 'react';
import ModalWrapper from '../../common/Modal/ModalWrapper';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { toggleModal } from '../../../reducers/modalReducer';
import { Divider } from '@mui/material';
import { setFileAllocateCompany, updateFileAllocation } from '../../../reducers/filesAllocationReducer';
import { getRequest, postRequest } from '../../../config';

const isObjectId = (str) => typeof str === 'string' && /^[0-9a-fA-F]{24}$/.test(str);

const FILE_TYPE_COLORS = { pdf: '#EF4444', docx: '#3B82F6', doc: '#3B82F6', xlsx: '#22C55E', xls: '#22C55E', pptx: '#F97316', ppt: '#F97316' };
const getExt = (name = '') => (name.split('.').pop() || '').toLowerCase();
const FileTypeBadge = ({ name }) => {
    const ext = getExt(name).toUpperCase();
    const color = FILE_TYPE_COLORS[ext.toLowerCase()] || '#6B7280';
    return <span style={{ fontSize: '10px', fontWeight: 700, color: '#fff', background: color, padding: '2px 5px', borderRadius: '3px', flexShrink: 0 }}>{ext || 'FILE'}</span>;
};

const UserAvatar = ({ user, profileClass }) => {
    if (!user) return null;
    const initials = user.firstName ? (user.firstName[0] + (user.lastName?.[0] || '')) : '?';
    const tooltip = user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '?';
    return user.profile
        ? <img src={user.profile} className={`w2rem h2rem df jcc aic br100 ${profileClass}`} data-tooltip={tooltip} alt={tooltip} />
        : <span className={`wsn w2rem h2rem df jcc aic br100 ${profileClass}`} data-tooltip={tooltip}>{initials}</span>;
};

// Build { qNo: questionText } from company.modules (old dashboard) + tempModule (new dashboard)
const buildQNoMap = (company, tempModule) => {
    const map = {};
    const allSections = [ ...Object.values(company?.modules || {}).flatMap(mod => mod.sections || []), ...(tempModule?.sections || []),];
    for (const sec of allSections) {
        for (const q of (sec.questions || [])) {
            if (q.qNo && q.qDescription) map[q.qNo.toUpperCase()] = q.qDescription;
        }
        for (const sub of Object.values(sec.subFolders || {})) {
            for (const q of (sub.questions || [])) {
                if (q.qNo && q.qDescription) map[q.qNo.toUpperCase()] = q.qDescription;
            }
            for (const nested of Object.values(sub.subFolders || {})) {
                for (const q of (nested.questions || [])) {
                    if (q.qNo && q.qDescription) map[q.qNo.toUpperCase()] = q.qDescription;
                }
            }
        }
    }
    return map;
};

const FileReportHistory = ({ modalType, reportHistories, setReportHistories, setFileReport, setCloneImportResult, setCurrentScore, company, profileClass, onRollbackComplete }) => {
    const dispatch = useDispatch();
    const tempModule = useSelector(state => state.fileAllocation?.tempModule);
    const qNoMap = React.useMemo(() => buildQNoMap(company, tempModule), [company, tempModule]);
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [expandedData, setExpandedData] = useState({});         // { [reportId]: { report, aiAnswers } }
    const [loadingIds, setLoadingIds] = useState(new Set());
    const [rollingBack, setRollingBack] = useState(false);

    const markRolledBack = (reportIds) => {
        if (setReportHistories) setReportHistories(prev => prev.map(r => reportIds.includes(String(r._id)) ? { ...r, rolledBack: true } : r));
        setExpandedData(prev => { const next = { ...prev }; reportIds.forEach(id => { if (next[id]) next[id] = { ...next[id], report: { ...next[id].report, rolledBack: true } }; }); return next; });
    };

    const removeAnswerFromLogs = (reportId, answerId) => {
        setExpandedData(prev => {
            if (!prev[reportId]) return prev;
            const updatedAnswers = prev[reportId].aiAnswers.map(a => String(a._id) === String(answerId) ? { ...a, rolledBack: true } : a);
            return { ...prev, [reportId]: { ...prev[reportId], aiAnswers: updatedAnswers } };
        });
    };

    const markModuleAnswersRolledBack = (reportId, moduleType) => {
        setExpandedData(prev => {
            if (!prev[reportId]) return prev;
            const updatedAnswers = prev[reportId].aiAnswers.map(a => a.moduleType === moduleType ? { ...a, rolledBack: true } : a);
            return { ...prev, [reportId]: { ...prev[reportId], aiAnswers: updatedAnswers } };
        });
    };

    const toggleExpand = async (report) => {
        const id = report._id;
        const next = new Set(expandedIds);
        if (next.has(id)) { next.delete(id); setExpandedIds(next); return; }
        next.add(id);
        setExpandedIds(next);
        if (expandedData[id]) return;

        setLoadingIds(prev => new Set(prev).add(id));
        try {
            const res = await getRequest(`/company/getFileReport/${id}`);
            const fullReport = res?.body || report;
            let aiAnswers = [];
            if (fullReport.runType === 'autoFill') {
                const logsRes = await getRequest(`/clarence/auto-fill/logs/${id}`);
                if (logsRes?.body?.items) aiAnswers = logsRes.body.items;
            }
            setExpandedData(prev => ({ ...prev, [id]: { report: fullReport, aiAnswers } }));
        } finally {
            setLoadingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
        }
    };

    const toggleSelect = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedIds(next);
    };

    const handleRollback = async (reportIds) => {
        setRollingBack(true);
        try {
            for (const reportId of reportIds) {
                await postRequest('/clarence/rollback-run', { fileReportId: reportId });
            }
            markRolledBack(reportIds.map(String));
            setSelectedIds(new Set());
            if (onRollbackComplete) onRollbackComplete();
        } finally {
            setRollingBack(false);
        }
    };

    // ── Expanded: File Import ──────────────────────────────────────
    const renderFileImportExpanded = (report, isRolledBack) => {
        const importResult = report.importResult || {};
        const entries = Object.values(importResult);
        const files = entries.map(e => e.file).filter(Boolean);

        // Invert: build question-centric map { qNo -> [file, ...] }
        // Each entry has file + section (array of qNos like ['D001.1', 'D001.5'])
        const qNoToFiles = {};
        entries.forEach(entry => {
            const qNos = entry.section?.filter(s => s && !s.includes('XXX')) || [];
            if (qNos.length === 0) {
                // unallocated file
                if (!qNoToFiles['__unallocated__']) qNoToFiles['__unallocated__'] = [];
                qNoToFiles['__unallocated__'].push({ file: entry.file, matchScore: entry.matchScore });
            } else {
                qNos.forEach(qNo => {
                    if (!qNoToFiles[qNo]) qNoToFiles[qNo] = [];
                    qNoToFiles[qNo].push({ file: entry.file, matchScore: entry.matchScore });
                });
            }
        });

        const allocatedQNos = Object.keys(qNoToFiles).filter(k => k !== '__unallocated__').sort();
        const unallocatedFiles = qNoToFiles['__unallocated__'] || [];
        const allocatedCount = allocatedQNos.length;
        const unallocatedCount = unallocatedFiles.length;

        return (
            <div style={{ padding: '1rem', borderTop: '1px solid #F3F4F6', background: '#FAFAFA' }}>
                {/* Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
                    {[
                        { label: 'Questions', value: allocatedQNos.length, color: '#1F2937', bg: '#F3F4F6' },
                        { label: 'Files allocated', value: allocatedCount, color: '#059669', bg: '#D1FAE5' },
                        { label: 'Unallocated', value: unallocatedCount, color: '#D97706', bg: '#FEF3C7' },
                        { label: 'Files imported', value: files.length, color: '#1F2937', bg: '#F3F4F6' },
                    ].map(s => (
                        <div key={s.label} style={{ background: s.bg, borderRadius: '8px', padding: '0.5rem', textAlign: 'center' }}>
                            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                            <div style={{ fontSize: '10px', color: s.color, opacity: 0.8 }}>{s.label}</div>
                        </div>
                    ))}
                </div>

                {/* One module accordion — question-centric rows */}
                <ModuleAccordion title={report.moduleName || 'Repository'} subtitle={`${allocatedCount} allocated · ${unallocatedCount} unallocated`} onRollback={isRolledBack ? null : () => postRequest('/clarence/rollback-run', { fileReportId: report._id }).then(() => { markRolledBack([String(report._id)]); if (onRollbackComplete) onRollbackComplete(); })} rollingBack={rollingBack}>
                    {allocatedQNos.map(qNo => {
                        const qFiles = qNoToFiles[qNo];
                        const questionName = qNoMap[qNo.toUpperCase()];
                        return (
                            <div key={qNo} style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid #F3F4F6' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '6px' }}>
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280' }}>{qNo}</span>
                                        {questionName && <span style={{ fontSize: '12px', fontWeight: 700, color: '#111827', marginLeft: '6px' }}>{questionName}</span>}
                                    </div>
                                    <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: '#D1FAE5', color: '#059669', flexShrink: 0 }}>
                                        {isRolledBack ? 'Rolled back' : 'Allocated'}
                                    </span>
                                    {!isRolledBack && (
                                        <button
                                            onClick={() => postRequest('/clarence/rollback-run', { fileReportId: report._id, answerId: qFiles[0]?.answerId, qNo }).then(() => { markRolledBack([String(report._id)]); if (onRollbackComplete) onRollbackComplete(); })}
                                            disabled={rollingBack}
                                            title="Rollback this question"
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', fontSize: '14px', color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '6px', cursor: 'pointer', flexShrink: 0, marginLeft: '4px' }}
                                        >↺</button>
                                    )}
                                </div>
                                <div style={{ padding: '0.4rem 0.6rem', background: '#EFF6FF', borderRadius: '6px', border: '1px solid #BFDBFE' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span style={{ fontSize: '10px', fontWeight: 700, color: '#3B82F6' }}>↑ FILES ALLOCATED</span>
                                        {qFiles[0]?.matchScore && <span style={{ fontSize: '10px', color: '#3B82F6' }}>{Math.round(qFiles[0].matchScore * 100)}% match</span>}
                                    </div>
                                    {qFiles.map((f, fi) => f.file && (
                                        <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: fi > 0 ? '3px' : 0 }}>
                                            <FileTypeBadge name={f.file.originalname} />
                                            <span style={{ fontSize: '11px', color: '#374151' }}>{f.file.originalname}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    {unallocatedFiles.map((f, i) => f.file && (
                        <div key={`unalloc-${i}`} style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid #F3F4F6' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <FileTypeBadge name={f.file.originalname} />
                                <span style={{ fontSize: '12px', color: '#6B7280', flex: 1 }}>{f.file.originalname}</span>
                                <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: '#FEF3C7', color: '#D97706', flexShrink: 0 }}>Unallocated</span>
                            </div>
                            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>No matching questions found for this file</div>
                        </div>
                    ))}
                </ModuleAccordion>

                <ImportedFilesList files={files} />
            </div>
        );
    };

    // ── Expanded: AutoFill ─────────────────────────────────────────
    const renderAutoFillExpanded = (report, aiAnswers, isRolledBack) => {
        const summary = report.autoFillSummary || {};
        const modules = summary.modules || [];
        const aiPct = summary.processed > 0 ? Math.round((summary.answered / summary.processed) * 100) : 0;
        const importedFiles = Object.values(report.importResult || {}).map(e => e.file).filter(Boolean);
        const fileUrlMap = {};
        importedFiles.forEach(f => { if (f.originalname && f.location) fileUrlMap[f.originalname] = f.location; });

        return (
            <div style={{ padding: '1rem', borderTop: '1px solid #F3F4F6', background: '#FAFAFA' }}>
                {/* Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
                    {[
                        { label: 'Processed', value: summary.processed || 0, color: '#1F2937', bg: '#F3F4F6' },
                        { label: 'Answered', value: summary.answered || 0, color: '#059669', bg: '#D1FAE5' },
                        { label: 'AI answered', value: `${aiPct}%`, color: '#3B82F6', bg: '#EFF6FF' },
                        { label: 'Skipped', value: summary.skipped || 0, color: '#6B7280', bg: '#F3F4F6' },
                    ].map(s => (
                        <div key={s.label} style={{ background: s.bg, borderRadius: '8px', padding: '0.5rem', textAlign: 'center' }}>
                            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                            <div style={{ fontSize: '10px', color: s.color, opacity: 0.8 }}>{s.label}</div>
                        </div>
                    ))}
                </div>

                {/* Module accordions with AI answers */}
                {modules.map(mod => {
                    const modAnswers = aiAnswers.filter(a => a.moduleType === mod.moduleType);
                    return (
                        <ModuleAccordion key={mod.moduleType} title={mod.moduleType} subtitle={`${mod.answered} answered · ${mod.skipped} skipped`} onRollback={isRolledBack ? null : () => postRequest('/clarence/rollback-run', { fileReportId: report._id, moduleTypes: [mod.moduleType] }).then(() => { markModuleAnswersRolledBack(String(report._id), mod.moduleType); if (onRollbackComplete) onRollbackComplete(); })} rollingBack={rollingBack}>
                            <div style={{ padding: '0 0.75rem 0.75rem' }}>
                            {modAnswers.length === 0
                                ? <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: '12px', padding: '1.5rem' }}>No AI answers found for this module.</div>
                                : modAnswers.map((a, i) => {
                                    const isAnswered = a.status === 'answered' || !!a.suggestedAnswer;
                                    const conf = a.confidence;
                                    const statusStyle = isAnswered
                                        ? { bg: '#ECFDF5', color: '#059669', border: '#A7F3D0', dot: '#34D399' }
                                        : { bg: '#F9FAFB', color: '#9CA3AF', border: '#E5E7EB', dot: '#D1D5DB' };
                                    const confStyle = conf >= 0.7
                                        ? { color: '#059669', bg: '#ECFDF5' }
                                        : conf >= 0.4
                                            ? { color: '#D97706', bg: '#FFFBEB' }
                                            : { color: '#9CA3AF', bg: '#F3F4F6' };
                                    const isAnswerRolledBack = isRolledBack || !!a.rolledBack;
                                    return (
                                        <div key={i} style={{ background: '#fff', borderRadius: '10px', padding: '0.75rem 0.875rem', marginBottom: '0.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)', borderLeft: `3px solid ${isAnswerRolledBack ? '#D1D5DB' : statusStyle.dot}` }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem' }}>
                                                <div style={{ flex: 1, lineHeight: '1.5' }}>
                                                    {a.qNo && <span style={{ fontSize: '10px', fontWeight: 700, color: '#9CA3AF', marginRight: '6px', letterSpacing: '0.03em', textTransform: 'uppercase' }}>{a.qNo}</span>}
                                                    <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#111827' }}>{a.question}</span>
                                                </div>
                                                <span style={{ fontSize: '10px', fontWeight: 600, padding: '3px 9px', borderRadius: '20px', flexShrink: 0, background: isAnswerRolledBack ? '#F3F4F6' : statusStyle.bg, color: isAnswerRolledBack ? '#6B7280' : statusStyle.color, border: `1px solid ${isAnswerRolledBack ? '#E5E7EB' : statusStyle.border}` }}>
                                                    {isAnswerRolledBack ? 'Rolled back' : (isAnswered ? 'Answered' : 'Skipped')}
                                                </span>
                                                {a._id && !isAnswerRolledBack && (
                                                    <button
                                                        onClick={() => postRequest('/clarence/rollback-run', { fileReportId: report._id, answerId: a._id }).then(() => { removeAnswerFromLogs(String(report._id), a._id); if (onRollbackComplete) onRollbackComplete(); })}
                                                        disabled={rollingBack}
                                                        title="Rollback this answer"
                                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', fontSize: '14px', color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '6px', cursor: 'pointer', flexShrink: 0 }}
                                                    >↺</button>
                                                )}
                                            </div>
                                            {(a.suggestedAnswer || a.comment) && (
                                                <div style={{ fontSize: '12px', color: '#374151', marginTop: '6px', lineHeight: '1.55', background: '#F9FAFB', borderRadius: '6px', padding: '7px 10px', borderLeft: `2px solid ${statusStyle.dot}` }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                                                        <svg width="10" height="10" viewBox="0 0 20 20" fill="none"><path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 3v5l3.5 3.5-1.1 1.1L8 10.5V5h2z" fill="#9CA3AF"/></svg>
                                                        <span style={{ fontSize: '10px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em' }}>AI answer</span>
                                                        {conf != null && (
                                                            <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 500, color: confStyle.color, background: confStyle.bg, padding: '1px 6px', borderRadius: '10px' }}>
                                                                {Math.round(conf * 100)}% confidence
                                                            </span>
                                                        )}
                                                    </div>
                                                    {a.suggestedAnswer && <div style={{ fontWeight: 600, marginBottom: a.comment ? '4px' : 0 }}>{a.suggestedAnswer}</div>}
                                                    {a.comment && <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: '1.5' }}>{a.comment}</div>}
                                                </div>
                                            )}
                                            {a.sources?.length > 0 && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                                                    {a.sources.map((src, si) => {
                                                        const url = fileUrlMap[src];
                                                        const chipStyle = { display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', background: '#EFF6FF', color: '#2563EB', borderRadius: '5px', padding: '2px 7px', fontWeight: 500, border: '1px solid #BFDBFE', cursor: 'pointer', textDecoration: 'none' };
                                                        const inner = <>
                                                            <svg width="9" height="9" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}><path d="M4 4h5v2H6v8h8v-3h2v5H4V4zm7-1h6v6h-2V6.414l-5.293 5.293-1.414-1.414L13.586 5H11V3z" fill="currentColor"/></svg>
                                                            {src}
                                                        </>;
                                                        return url
                                                            ? <a key={si} href={url} target="_blank" rel="noopener noreferrer" style={chipStyle}>{inner}</a>
                                                            : <span key={si} style={{ ...chipStyle, cursor: 'default' }}>{inner}</span>;
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            }
                            </div>
                        </ModuleAccordion>
                    );
                })}

                <ImportedFilesList files={importedFiles} />
            </div>
        );
    };

    const selectedCount = selectedIds.size;

    return (
        <ModalWrapper modalType={modalType} modalCardClasses="p2rem maw1500px w90vw mah90">
            <div className='df fdc jcfs pr'>
                <div className='df jcfs aic'>
                    <img src={require("../../../../images/dataRepo/file.svg")} alt="fileManagement" />
                    <h1 className="c15 fw600 fs20px mb0rem ml0-5rem">Filing Assistant History</h1>
                </div>
                <h1 className='c3 fs18px w100 fw400 mt0-5rem tal'>Filing Assistant</h1>
            </div>

            <Divider style={{ marginTop: '16px', marginBottom: '16px' }} />

            {reportHistories.length === 0 ? (
                <p className="w100 fs1-5rem tac py2rem">No file report history</p>
            ) : (
                <div style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '0.4rem 1rem', marginBottom: '0.5rem' }}>
                        <div style={{ flex: '0 0 28px' }} />
                        <div style={{ flex: '0 0 16%', fontSize: '12px', color: '#1F2937', fontWeight: 600 }}>Run By</div>
                        <div style={{ flex: '0 0 16%', fontSize: '12px', color: '#1F2937', fontWeight: 600 }}>Date</div>
                        <div style={{ flex: '0 0 11%', fontSize: '12px', color: '#1F2937', fontWeight: 600 }}>Type</div>
                        <div style={{ flex: '0 0 13%', fontSize: '12px', color: '#1F2937', fontWeight: 600 }}>Module</div>
                        <div style={{ flex: '0 0 6%', fontSize: '12px', color: '#1F2937', fontWeight: 600 }}>Files</div>
                        <div style={{ flex: '0 0 11%', fontSize: '12px', color: '#1F2937', fontWeight: 600 }}>Answered</div>
                        <div style={{ flex: '0 0 10%', fontSize: '12px', color: '#1F2937', fontWeight: 600 }}>Status</div>
                        <div style={{ flex: 1, fontSize: '12px', color: '#1F2937', fontWeight: 600, textAlign: 'center' }}>Actions</div>
                        <div style={{ flex: '0 0 32px' }} />
                    </div>

                    {reportHistories.map(report => {
                        const id = report._id;
                        const isExpanded = expandedIds.has(id);
                        const isSelected = selectedIds.has(id);
                        const isLoading = loadingIds.has(id);
                        const data = expandedData[id];
                        const isAutoFill = report.runType === 'autoFill';

                        // Module chips
                        const mods = report.autoFillSummary?.modules || [];
                        const moduleChips = mods.length === 0
                            ? (report.moduleName ? [report.moduleName] : [])
                            : mods.length === 1 ? [mods[0].moduleType]
                            : [`${mods[0].moduleType} +${mods.length - 1}`];

                        // Files count
                        const filesCount = report.importResultCount || 0;

                        // Answered/Questions count
                        const answeredLabel = isAutoFill
                            ? (report.autoFillSummary?.answered != null ? `${report.autoFillSummary.answered} answered` : '—')
                            : (report.allocatedQuestionCount != null ? `${report.allocatedQuestionCount} questions` : '—');

                        // Status
                        const isRolledBack = !!report.rolledBack;
                        const isComplete = report.status === 'Complete';
                        const isInProgress = report.status === 'In Progress';

                        // Run by user
                        const runByUser = report.importBy;
                        const runByName = runByUser?.firstName ? `${runByUser.firstName} ${runByUser.lastName || ''}`.trim() : null;

                        return (
                            <div key={id} style={{ boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)', borderRadius: '8px', marginBottom: '0.65rem', overflow: 'hidden', background: isSelected ? '#EFF6FF' : '#fff' }}>
                                {/* Collapsed row */}
                                <div onClick={() => toggleExpand(report)} style={{ display: 'flex', alignItems: 'center', padding: '0.65rem 1rem', cursor: 'pointer' }}>
                                    <div style={{ flex: '0 0 28px', display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(id)} style={{ accentColor: '#3B82F6', width: '15px', height: '15px', cursor: 'pointer' }} />
                                    </div>
                                    {/* Run By */}
                                    <div style={{ flex: '0 0 16%', display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                                        <UserAvatar user={runByUser} profileClass={profileClass} />
                                        {runByName && <span style={{ fontSize: '12px', color: '#1F2937', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{runByName}</span>}
                                    </div>
                                    {/* Date */}
                                    <div style={{ flex: '0 0 16%' }}>
                                        <span style={{ fontSize: '12px', color: '#1F2937', fontWeight: 500 }}>
                                            {moment(report.importDate).format('MMM D, YYYY h:mm A')}
                                        </span>
                                        {isLoading && <span style={{ fontSize: '11px', color: '#9CA3AF', marginLeft: '6px' }}>Loading...</span>}
                                    </div>
                                    {/* Type badge */}
                                    <div style={{ flex: '0 0 11%' }}>
                                        {isAutoFill
                                            ? <span style={{ fontSize: '11px', fontWeight: 600, color: '#059669', background: '#D1FAE5', padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>✪ Auto-fill</span>
                                            : <span style={{ fontSize: '11px', fontWeight: 600, color: '#3B82F6', background: '#EFF6FF', padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>↑ File Import</span>
                                        }
                                    </div>
                                    {/* Module chips */}
                                    <div style={{ flex: '0 0 13%', minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {moduleChips.length === 0 ? <span style={{ fontSize: '12px', color: '#9CA3AF' }}>—</span>
                                            : moduleChips.map((chip, i) => (
                                                <span key={i} style={{ fontSize: '10px', fontWeight: 700, color: '#1E40AF', background: '#EFF6FF', border: '1px solid #BFDBFE', padding: '2px 7px', borderRadius: '20px', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{chip}</span>
                                            ))
                                        }
                                    </div>
                                    {/* Files */}
                                    <div style={{ flex: '0 0 6%' }}>
                                        <span style={{ fontSize: '12px', color: '#6B7280' }}>{filesCount > 0 ? `${filesCount}` : '—'}</span>
                                    </div>
                                    {/* Answered / Questions */}
                                    <div style={{ flex: '0 0 11%' }}>
                                        <span style={{ fontSize: '12px', color: '#059669', fontWeight: 500 }}>{answeredLabel}</span>
                                    </div>
                                    {/* Status */}
                                    <div style={{ flex: '0 0 10%' }}>
                                        {isRolledBack
                                            ? <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap', background: '#F3F4F6', color: '#6B7280' }}>Rolled back</span>
                                            : <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap', background: isComplete ? '#D1FAE5' : isInProgress ? '#FEF3C7' : '#F3F4F6', color: isComplete ? '#059669' : isInProgress ? '#D97706' : '#6B7280' }}>
                                                {isComplete ? 'Completed' : isInProgress ? 'In Progress' : '—'}
                                            </span>
                                        }
                                    </div>
                                    {/* Actions */}
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
                                        {!isRolledBack && (
                                            <button
                                                onClick={() => handleRollback([id])}
                                                disabled={rollingBack}
                                                title="Rollback this run — removes all answers and file allocations"
                                                style={{ background: '#FEF2F2', border: '1px solid #FECACA', cursor: 'pointer', width: '28px', height: '28px', borderRadius: '6px', color: '#DC2626', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >↺</button>
                                        )}
                                    </div>
                                    {/* Expand chevron */}
                                    <div style={{ flex: '0 0 32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: '#9CA3AF' }}>
                                            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>

                                {/* Expanded content */}
                                {isExpanded && data && (
                                    isAutoFill
                                        ? renderAutoFillExpanded(data.report, data.aiAnswers, isRolledBack)
                                        : renderFileImportExpanded(data.report, isRolledBack)
                                )}
                                {isExpanded && isLoading && (
                                    <div style={{ padding: '1.5rem', textAlign: 'center', color: '#9CA3AF', fontSize: '12px', borderTop: '1px solid #F3F4F6' }}>Loading details...</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Bulk rollback bar */}
            {selectedCount > 0 && (
                <div style={{ marginTop: '1rem', padding: '0.65rem 0.75rem', background: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: '#DC2626', fontWeight: 500 }}>{selectedCount} run{selectedCount > 1 ? 's' : ''} selected for rollback</span>
                    <button onClick={() => handleRollback([...selectedIds])} disabled={rollingBack} style={{ fontSize: '12px', fontWeight: 600, padding: '5px 16px', borderRadius: '6px', background: '#DC2626', color: '#fff', border: 'none', cursor: 'pointer' }}>
                        {rollingBack ? 'Rolling back...' : 'Rollback'}
                    </button>
                </div>
            )}
        </ModalWrapper>
    );
};

// ── Shared sub-components ──────────────────────────────────────────

const ModuleAccordion = ({ title, subtitle, children, onRollback, rollingBack }) => {
    const [open, setOpen] = useState(true);
    return (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', marginBottom: '0.5rem', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', background: '#fff', borderLeft: '3px solid #3B82F6' }}>
                <span onClick={() => setOpen(o => !o)} style={{ fontSize: '13px', fontWeight: 600, color: '#1F2937', flex: 1, cursor: 'pointer' }}>{title}</span>
                <span style={{ fontSize: '11px', color: '#6B7280' }}>{subtitle}</span>
                {onRollback && (
                    <button
                        onClick={e => { e.stopPropagation(); onRollback(); }}
                        disabled={rollingBack}
                        title="Rollback this module"
                        style={{ fontSize: '11px', fontWeight: 600, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '6px', padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >↺ Rollback</button>
                )}
                <svg onClick={() => setOpen(o => !o)} width="12" height="12" viewBox="0 0 20 20" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: '#9CA3AF', cursor: 'pointer', flexShrink: 0 }}>
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </div>
            {open && <div style={{ maxHeight: '280px', overflowY: 'auto', paddingTop: '0.5rem' }}>{children}</div>}
        </div>
    );
};

const ImportedFilesList = ({ files }) => {
    if (!files?.length) return null;
    return (
        <div style={{ marginTop: '0.75rem' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '0.4rem' }}>IMPORTED FILES ({files.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {files.map((file, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem', background: '#fff', border: '1px solid #E5E7EB', borderRadius: '6px' }}>
                        <FileTypeBadge name={file.originalname} />
                        <span style={{ flex: 1, fontSize: '12px', color: '#1F2937' }}>{file.originalname}</span>
                        {file.pages && <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{file.pages} pages</span>}
                        {file.location && (
                            <a href={file.location} target="_blank" rel="noreferrer" style={{ fontSize: '11px', color: '#3B82F6', textDecoration: 'none', flexShrink: 0 }}>View →</a>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FileReportHistory;
